import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, extractErrorMessage } from "../lib/api";
import { ChevronLeft, Plus, Trash2, X, ShieldCheck, UserPlus } from "lucide-react";

const APPS = [
  { id: "otc", label: "4.0 · OTC", group: "envicom_4_0" },
  { id: "version_control", label: "4.0 · Version Control", group: "envicom_4_0" },
  { id: "controllers", label: "4.0 · Controllers", group: "envicom_4_0" },
  { id: "envicom_demo", label: "4.0 · Demo", group: "envicom_4_0" },
  { id: "envicom_lite_versions", label: "Lite · Version Control", group: "envicom_lite" },
  { id: "envicom_lite_controllers", label: "Lite · Controllers", group: "envicom_lite" },
];

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [error, setError] = useState("");
  const [showInvite, setShowInvite] = useState(false);
  const [editing, setEditing] = useState(null); // user object being edited
  const [draft, setDraft] = useState({ email: "", name: "", password: "", permissions: {} });

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      setError(extractErrorMessage(e));
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const onInvite = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...draft });
      setShowInvite(false);
      setDraft({ email: "", name: "", password: "", permissions: {} });
      fetchUsers();
    } catch (err) { setError(extractErrorMessage(err)); }
  };

  const onSavePerms = async (e) => {
    e.preventDefault();
    if (!editing) return;
    try {
      const payload = { permissions: draft.permissions };
      if (draft.name) payload.name = draft.name;
      if (draft.password) payload.password = draft.password;
      await api.patch(`/users/${editing.id}`, payload);
      setEditing(null);
      fetchUsers();
    } catch (err) { setError(extractErrorMessage(err)); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Remove access for ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      fetchUsers();
    } catch (err) { setError(extractErrorMessage(err)); }
  };

  const setPerm = (appId, level) => {
    setDraft((d) => {
      const next = { ...d.permissions };
      if (!level) delete next[appId];
      else next[appId] = level;
      return { ...d, permissions: next };
    });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 relative z-10">
        <Link to="/" data-testid="users-back-link" className="inline-flex items-center gap-1 text-envicom-muted hover:text-envicom-text font-mono text-sm">
          <ChevronLeft className="w-4 h-4" /> Back to tools
        </Link>

        <div className="mt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-mono text-2xl md:text-3xl font-bold tracking-tight">User Management</h1>
            <p className="text-envicom-muted text-sm">Invite team members and grant per-app read or read/write access.</p>
          </div>
          <button
            type="button"
            onClick={() => { setDraft({ email: "", name: "", password: "", permissions: {} }); setShowInvite(true); }}
            data-testid="invite-user-btn"
            className="inline-flex items-center gap-2 px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm"
          >
            <UserPlus className="w-4 h-4" /> Invite user
          </button>
        </div>

        {error && (
          <div data-testid="users-error" className="mt-4 px-3 py-2 rounded bg-envicom-error/10 border border-envicom-error/30 text-envicom-error text-sm">
            {error}
          </div>
        )}

        <div className="mt-8 bg-envicom-surface border border-envicom-border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="bg-envicom-bg/50 text-envicom-muted text-[11px] uppercase tracking-wider font-mono">
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Role</th>
                  {APPS.map((a) => <th key={a.id} className="text-left p-3">{a.label}</th>)}
                  <th className="p-3 w-32" />
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-envicom-border hover:bg-envicom-surfaceHover/40">
                    <td className="p-3">
                      <div className="text-envicom-text font-medium">{u.name}</div>
                      <div className="text-envicom-muted text-[12px] font-mono">{u.email}</div>
                    </td>
                    <td className="p-3">
                      {u.role === "super_admin" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-envicom-accent/50 text-envicom-accent text-[11px] font-mono uppercase tracking-wider">
                          <ShieldCheck className="w-3 h-3" /> Super admin
                        </span>
                      ) : (
                        <span className="text-envicom-muted text-xs font-mono">User</span>
                      )}
                    </td>
                    {APPS.map((a) => {
                      const lvl = u.role === "super_admin" ? "rw" : (u.permissions || {})[a.id];
                      return (
                        <td key={a.id} className="p-3 font-mono text-xs">
                          {lvl === "rw" && <span className="px-2 py-0.5 rounded border border-envicom-green/40 text-envicom-green bg-envicom-green/10">Read/Write</span>}
                          {lvl === "r" && <span className="px-2 py-0.5 rounded border border-envicom-blue/40 text-envicom-blue bg-envicom-blue/10">Read</span>}
                          {!lvl && <span className="text-envicom-muted">—</span>}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right space-x-3 whitespace-nowrap">
                      {u.role !== "super_admin" && (
                        <>
                          <button type="button" onClick={() => { setEditing(u); setDraft({ email: u.email, name: u.name, password: "", permissions: { ...(u.permissions || {}) } }); }} className="text-envicom-muted hover:text-envicom-text text-xs font-mono" data-testid={`user-edit-${u.id}`}>Edit</button>
                          <button type="button" onClick={() => onDelete(u)} className="text-envicom-muted hover:text-envicom-error" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4 inline" /></button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {(showInvite || editing) && (
        <Modal onClose={() => { setShowInvite(false); setEditing(null); }} title={editing ? `Edit ${editing.email}` : "Invite new user"}>
          <form onSubmit={editing ? onSavePerms : onInvite} className="space-y-4" data-testid="user-form">
            {!editing && (
              <Field label="Email">
                <input required type="email" value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={inputCls} data-testid="user-email-input" />
              </Field>
            )}
            <Field label="Full name">
              <input required value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className={inputCls} data-testid="user-name-input" />
            </Field>
            <Field label={editing ? "New password (leave blank to keep)" : "Password"}>
              <input required={!editing} type="text" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} className={inputCls} data-testid="user-password-input" placeholder={editing ? "" : "Initial password"} />
            </Field>

            <div>
              <div className="text-envicom-muted text-xs uppercase tracking-wider font-mono mb-2">App permissions</div>
              <div className="space-y-2">
                {APPS.map((a) => (
                  <div key={a.id} className="flex items-center justify-between bg-envicom-input border border-envicom-border rounded px-3 py-2">
                    <span className="font-mono text-sm text-envicom-text">{a.label}</span>
                    <div className="flex gap-1">
                      {[
                        { val: null, label: "None" },
                        { val: "r", label: "Read" },
                        { val: "rw", label: "R/W" },
                      ].map((opt) => {
                        const active = (draft.permissions[a.id] || null) === opt.val;
                        return (
                          <button
                            key={String(opt.val)}
                            type="button"
                            onClick={() => setPerm(a.id, opt.val)}
                            data-testid={`perm-${a.id}-${opt.val || "none"}`}
                            className={`px-2.5 py-1 rounded text-[11px] font-mono uppercase tracking-wider border transition-colors
                              ${active ? "border-envicom-accent bg-envicom-accent/15 text-envicom-accent" : "border-envicom-border text-envicom-muted hover:text-envicom-text"}`}
                          >
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setShowInvite(false); setEditing(null); }} className="px-4 py-2 rounded border border-envicom-border text-envicom-muted text-sm font-mono">Cancel</button>
              <button type="submit" data-testid="user-submit-btn" className="px-4 py-2 rounded bg-envicom-accent hover:bg-sky-400 text-black font-semibold font-mono text-sm">
                {editing ? "Save" : "Invite"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

const inputCls = "w-full bg-envicom-input border border-envicom-border rounded px-3 py-2 text-envicom-text focus:outline-none focus:border-envicom-accent text-sm";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-envicom-muted text-xs uppercase tracking-wider font-mono">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function Modal({ children, title, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 animate-fade-up" onClick={onClose}>
      <div className="bg-envicom-surface border border-envicom-border rounded-md w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-envicom-border">
          <h2 className="font-mono font-bold text-envicom-text">{title}</h2>
          <button onClick={onClose} className="text-envicom-muted hover:text-envicom-text" data-testid="user-modal-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}
